import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldCheck, Loader2 } from "lucide-react";

export default function ClaimAccount() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { applySession } = useAuth();
  const [state, setState] = useState({ loading: true, error: "", claim: null });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    api.get(`/player-claims/${token}`)
      .then(({ data }) => setState({ loading: false, error: "", claim: data }))
      .catch((e) => setState({ loading: false, error: formatApiError(e?.response?.data?.detail) || "This invite link is invalid.", claim: null }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (password.length < 8) { setFormError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setFormError("Passwords do not match."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/player-claims/${token}/claim`, { password });
      applySession(data);
      navigate("/my-profile");
    } catch (err) {
      setFormError(formatApiError(err?.response?.data?.detail) || "Could not complete sign-up.");
    } finally { setBusy(false); }
  };

  return (
    <PublicLayout>
      <div className="min-h-[calc(100vh-200px)] bg-[#F8F9FA] flex items-center justify-center py-16 px-6">
        <div className="w-full max-w-md">
          <div className="bg-white border border-slate-200 p-10 rounded-sm" data-testid="claim-account-card">
            <div className="flex items-center gap-2 label-overline text-[#E63946]"><ShieldCheck className="w-4 h-4" /> Player Account</div>

            {state.loading ? (
              <div className="mt-6 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Checking your invite…</div>
            ) : state.error ? (
              <>
                <h1 className="font-display text-3xl uppercase tracking-tighter mt-2">Invite Unavailable</h1>
                <div data-testid="claim-error" className="mt-4 border-l-4 border-[#E63946] bg-red-50 p-3 text-sm text-[#E63946]">{state.error}</div>
                <Link to="/login" className="inline-block mt-6 text-xs font-semibold uppercase tracking-wider text-[#0A192F]">← Back to Sign In</Link>
              </>
            ) : (
              <>
                <h1 className="font-display text-4xl uppercase tracking-tighter mt-2">Claim Your Profile</h1>
                <p className="text-sm text-slate-600 mt-2">
                  Welcome{state.claim?.player ? `, ${state.claim.player.first_name} ${state.claim.player.last_name}` : ""}! Set a password to access your CBR Prospects player dashboard.
                </p>
                <p className="text-xs text-slate-400 mt-1">{state.claim?.email}</p>

                {formError && <div data-testid="claim-form-error" className="mt-6 border-l-4 border-[#E63946] bg-red-50 p-3 text-sm text-[#E63946]">{formError}</div>}

                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div>
                    <label className="label-overline">Create Password</label>
                    <input data-testid="claim-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 w-full px-4 py-3 border border-slate-300 rounded-sm focus:border-[#0A192F]" />
                  </div>
                  <div>
                    <label className="label-overline">Confirm Password</label>
                    <input data-testid="claim-confirm-input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-2 w-full px-4 py-3 border border-slate-300 rounded-sm focus:border-[#0A192F]" />
                  </div>
                  <button data-testid="claim-submit-button" type="submit" disabled={busy} className="w-full bg-[#0A192F] hover:bg-[#1a2a4a] text-white py-3 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60">
                    {busy ? "Creating Account…" : "Create Account & Continue"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
